import { centsToInput, parseMoney } from '@/lib/format';
import type { FieldValue, ListField } from '@/types/platform';

export interface LocationInput {
  lat: string;
  lng: string;
}

// What an input holds while a person edits: text for most fields, a flag for a
// checkbox, and two numbers as text for a point on the map.
export type InputValue = string | boolean | LocationInput;

// What the server is sent for one field. Money is whole cents. A point is both
// numbers together. Nothing entered is null. This is a type alias, not an
// interface, so it fits the request payload type.
export type SubmittedValue =
  | string
  | number
  | boolean
  | { lat: number | null; lng: number | null }
  | null;

export function toInput(field: ListField, stored: FieldValue | undefined): InputValue {
  if (field.type === 'checkbox') return stored === true;

  if (field.type === 'location') {
    return typeof stored === 'object' && stored !== null
      ? { lat: String(stored.lat), lng: String(stored.lng) }
      : { lat: '', lng: '' };
  }

  if (field.type === 'money') return typeof stored === 'number' ? centsToInput(stored) : '';

  if (stored === null || stored === undefined || typeof stored === 'object') return '';

  return String(stored);
}

export function toSubmitted(field: ListField, input: InputValue): SubmittedValue {
  if (typeof input === 'boolean') return input;

  if (typeof input === 'object') {
    const lat = input.lat.trim();
    const lng = input.lng.trim();

    // One half of a point is sent as it is, so the server can say what is missing.
    return lat === '' && lng === ''
      ? null
      : { lat: lat === '' ? null : Number(lat), lng: lng === '' ? null : Number(lng) };
  }

  if (field.type === 'money') return parseMoney(input);
  if (field.type === 'choice' || field.type === 'customer') return input === '' ? null : input;

  return input;
}

// Dollars that cannot be read as an amount are caught here, before they could
// be sent as "nothing entered" and wipe a stored amount.
export function moneyProblem(field: ListField, input: InputValue): string | null {
  const unreadable =
    field.type === 'money' &&
    typeof input === 'string' &&
    input.trim() !== '' &&
    parseMoney(input) === null;

  return unreadable ? `${field.label} must be a dollar amount, like 1840.50` : null;
}
